import { useState } from 'react'
import { format } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useCustodyExceptions } from '../hooks/useCustody'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'

const REASONS = [
  { id: 'holiday',      label: 'Holiday' },
  { id: 'school_event', label: 'School Event' },
  { id: 'vacation',     label: 'Vacation' },
  { id: 'other',        label: 'Other' },
]

export const ExceptionModal = ({ schedule, prefillDate, originalHouseholdId, onClose }) => {
  const { household, user } = useAuth()
  const { activeLink }       = useLinkedHouseholds()
  const { create }           = useCustodyExceptions(schedule?.id)

  const linkedHouseholdId = activeLink
    ? (activeLink.primary_household_id === household?.id
        ? activeLink.linked_household_id
        : activeLink.primary_household_id)
    : null

  const ourName   = household?.name ?? 'Your House'
  const otherName = activeLink?.otherHousehold?.name ?? 'Other House'

  const [date,          setDate]          = useState(prefillDate ? format(prefillDate, 'yyyy-MM-dd') : '')
  const [exceptionTo,   setExceptionTo]   = useState(
    originalHouseholdId === household?.id ? 'other' : 'ours'
  )
  const [reason,  setReason]  = useState('other')
  const [notes,   setNotes]   = useState('')
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')

  const handleSave = async () => {
    if (!date) { setError('Select a date.'); return }
    setSaving(true)
    setError('')
    try {
      await create.mutateAsync({
        child_id:               schedule.child_id,
        exception_date:         date,
        original_household_id:  originalHouseholdId ?? household.id,
        exception_household_id: exceptionTo === 'ours' ? household.id : linkedHouseholdId,
        reason,
        notes: notes.trim() || null,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto shadow-card p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-text-primary">Add Exception</h3>

        <div>
          {lbl('Date')}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        </div>

        <div>
          {lbl('Child Stays With')}
          <div className="flex gap-2">
            {[
              { id: 'ours',  label: ourName },
              { id: 'other', label: otherName },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setExceptionTo(id)}
                className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                  exceptionTo === id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          {lbl('Reason')}
          <div className="flex flex-wrap gap-2">
            {REASONS.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setReason(r.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  reason === r.id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          {lbl('Notes')}
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional details…"
            className={inputCls}
          />
        </div>

        {error && <p className="text-sm text-rose-600">{error}</p>}

        <div className="flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save Exception'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}
